import { http } from '@pendo/http';
import { i18n_t as $t } from '@/utils/i18n';
import get from 'lodash/get';
import { getDefaultPeriodFilters } from '@/retention/helpers/retention-date-helper';
import moment from '@utils/moment-wrapper';
import momentTimeZone from 'moment-timezone';

function buildUrlWithParams (baseUrl, options = {}) {
    const allowedParams = ['streaming', 'revision', 'force'];
    const params = new URLSearchParams();

    allowedParams.forEach((param) => {
        if (options[param] !== undefined && options[param] !== null) {
            params.set(param, options[param]);
        }
    });

    return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

export function getRetentionInsights (requestBody, options = {}) {
    const url = buildUrlWithParams('/api/streaming/s/_SID_/insight/generate', options);

    const requestOptions = {
        responseType: 'stream',
        adapter: 'fetch',
    };

    if (options.signal) {
        requestOptions.signal = options.signal;
    }

    return http.post(url, requestBody, requestOptions).then((res) => res.data);
}

export function getInitialState () {
    return {
        insights: null,
        insightRequestBody: null,
        isLoading: false,
        error: null,
    };
}

function getFirstAvailableAppId (appList) {
    if (!appList || appList.length === 0) {
        return null;
    }

    const firstItem = appList[0];
    if (firstItem?.apps && firstItem.apps.length > 0) {
        return firstItem.apps[0]?.id || null;
    }

    return firstItem?.id || null;
}

export function determineAppId (rootGetters, reportIndex = 0) {
    const trendsDataSettings = rootGetters['userSettings/settingByName']('trends-data-settings', true);

    if (trendsDataSettings) {
        const { appId } = trendsDataSettings;
        if (appId) {
            return appId;
        }
    }

    const retentionHistory = rootGetters['savedReports/retentionHistory'] || [];

    if (retentionHistory.length > 0 && reportIndex < retentionHistory.length) {
        const mostRecentReportId = retentionHistory[reportIndex].id;
        const mostRecentReport = rootGetters['savedReports/reportById']?.(mostRecentReportId);
        if (mostRecentReport?.appId === 'all') {
            reportIndex++;

            return determineAppId(rootGetters, reportIndex);
        }

        if (mostRecentReport?.appId) {
            const { appId } = mostRecentReport;
            if (typeof appId === 'number' || typeof appId === 'string') {
                return appId;
            }
        }
    }

    const availableApps = rootGetters['apps/analyticsAppsListWithIcons'];

    if (availableApps?.length > 0) {
        return getFirstAvailableAppId(availableApps);
    }

    return null;
}

export function buildRequestBody (appId, includeAnonymous = false, rootGetters = null) {
    const subscription = rootGetters?.['subscription/getActiveSub'];
    let { periodStart, periodEnd } = getDefaultPeriodFilters(get(subscription, 'firstEventTime'));

    let periodStartMoment = moment(periodStart, { ignoreTimeZone: true });
    let periodEndMoment = moment(periodEnd, { ignoreTimeZone: true });

    const zone = get(subscription, 'timeZone');
    if (zone) {
        periodStartMoment = momentTimeZone.tz(periodStartMoment.format('YYYY-MM'), zone);
        periodEndMoment = momentTimeZone.tz(periodEndMoment.format('YYYY-MM'), zone);
    }

    periodStart = periodStartMoment.valueOf();
    periodEnd = periodEndMoment.valueOf();

    return {
        job_spec: {
            app_id: appId,
            metrics: [
                {
                    metric_type: 'new_vs_existing',
                    kind: 'visitor',
                },
                {
                    metric_type: 'month_over_month',
                    kind: 'account',
                },
            ],
            trend_type: 'retention',
            include_anonymous: includeAnonymous,
            start_timestamp: periodStart,
            end_timestamp: periodEnd,
        },
    };
}

export function buildAppDataFromRetentionState (state, rootGetters, fallbackAppId = null) {
    const retentionInsightsArtifact = state?.artifacts?.retention_insights;
    const responseAppId = retentionInsightsArtifact?.data?.app_id || fallbackAppId;

    const app = rootGetters['apps/getAppById']?.(responseAppId);
    const appIcon = rootGetters['apps/getIconByAppId']?.(responseAppId);
    const appData = {
        name: app?.displayName || app?.name,
        icon: appIcon,
        appId: responseAppId,
    };

    return appData;
}

function parseSummary (summary, _tagName) {
    if (!summary || typeof summary !== 'string') {
        return { textBefore: summary || '', textAfter: '', showAppTag: false };
    }

    const tagNamePlaceholder = '{{APP_NAME}}';
    const placeholderIndex = summary.indexOf(tagNamePlaceholder);

    if (placeholderIndex !== -1) {
        const textBefore = summary.substring(0, placeholderIndex).trim();
        const textAfter = summary.substring(placeholderIndex + tagNamePlaceholder.length).trim();

        return { textBefore, textAfter, showAppTag: true };
    }

    return { textBefore: summary, textAfter: '', showAppTag: false };
}

export function transformInsightsResponse (finalState, appData) {
    const retentionInsightsArtifact = finalState?.artifacts?.retention_insights;
    const retentionInsightsData = retentionInsightsArtifact?.data;
    if (!retentionInsightsData?.results || !Array.isArray(retentionInsightsData.results)) {
        return [];
    }

    const tagName = appData?.name;
    const rawIcon = appData?.icon;
    const appIcon = typeof rawIcon === 'string' ? { type: rawIcon, size: 12 } : { ...rawIcon, size: 12 };

    return retentionInsightsData.results
        .map((result) => {
            const { metric, kind } = result;

            const title = kind === 'account' ? $t('trends.Account retention') : $t('trends.Visitor retention');

            if (metric === 'new_vs_existing') {
                const { new_retention: newUsersPercentage, industry_retention: existingUsersPercentage } = result;

                const { textBefore, textAfter, showAppTag } = parseSummary(result.summary, tagName);

                return {
                    title,
                    newUsersPercentage,
                    existingUsersPercentage,
                    delta: undefined,
                    textBeforeTagName: textBefore,
                    textAfterTagName: textAfter,
                    showAppTag,
                    tagName,
                    appIcon,
                    kind,
                    appId: appData?.appId,
                };
            }

            if (metric === 'month_over_month') {
                const {
                    current_retention: newUsersPercentage,
                    previous_retention: existingUsersPercentage,
                    delta,
                } = result;
                const { textBefore, textAfter, showAppTag } = parseSummary(result.summary, tagName);

                return {
                    title,
                    newUsersPercentage,
                    existingUsersPercentage,
                    delta,
                    textBeforeTagName: textBefore,
                    textAfterTagName: textAfter,
                    showAppTag,
                    tagName,
                    appIcon,
                    kind,
                    appId: appData?.appId,
                };
            }

            return null;
        })
        .filter(Boolean);
}
